import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Clock,
  Send,
  AlertTriangle,
  CheckCircle,
  Mail,
  User,
  Calendar,
  Loader2,
  RefreshCw,
} from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { reminderApi } from "@/services/api";
import { useAuthStore } from "@/store/auth.store";
import { formatDateShort } from "@/utils/dateFormat";

// Stat card component
const StatCard = ({
  title,
  value,
  description,
  icon: Icon,
  variant = "default",
}) => {
  const variants = {
    default: "bg-primary/10 text-primary",
    warning: "bg-amber-500/10 text-amber-600",
    success: "bg-green-500/10 text-green-600",
  };

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className="text-3xl font-bold mt-1">{value}</p>
            {description && (
              <p className="text-xs text-muted-foreground mt-1">
                {description}
              </p>
            )}
          </div>
          <div className={`p-3 rounded-full ${variants[variant]}`}>
            <Icon className="h-6 w-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Recipient row component
const RecipientRow = ({ recipient, envelopeId, onSendReminder, sending }) => {
  const { t } = useTranslation();
  const user = useAuthStore((state) => state.user);

  const isOverdue =
    recipient.nextReminderAt && new Date(recipient.nextReminderAt) < new Date();

  const getStatusBadge = () => {
    switch (recipient.status) {
      case "viewed":
        return (
          <Badge
            variant="outline"
            className="bg-violet-50 text-violet-700 border-violet-200"
          >
            Viewed
          </Badge>
        );
      case "delivered":
        return (
          <Badge
            variant="outline"
            className="bg-blue-50 text-blue-700 border-blue-200"
          >
            Delivered
          </Badge>
        );
      case "sent":
        return (
          <Badge
            variant="outline"
            className="bg-sky-50 text-sky-700 border-sky-200"
          >
            Sent
          </Badge>
        );
      default:
        return (
          <Badge
            variant="outline"
            className="bg-slate-50 text-slate-700 border-slate-200"
          >
            Pending
          </Badge>
        );
    }
  };

  return (
    <div className="flex items-center justify-between py-3 px-4 hover:bg-muted/50 rounded-lg transition-colors dark:bg-background">
      <div className="flex items-center gap-3 min-w-0">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted">
          <User className="h-4 w-4 text-muted-foreground" />
        </div>
        <div className="min-w-0">
          <p className="font-medium text-sm truncate">
            {recipient.name || recipient.email}
          </p>
          <p className="text-xs text-muted-foreground truncate">
            {recipient.email}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-4">
        {getStatusBadge()}

        <div className="text-right hidden sm:block">
          <p className="text-xs text-muted-foreground">
            {recipient.reminderCount > 0 ? (
              <>
                {t("reminders.remindersSent", "{{count}} reminder(s)", {
                  count: recipient.reminderCount,
                })}
              </>
            ) : (
              t("reminders.noRemindersSent", "No reminders sent")
            )}
          </p>
          {recipient.nextReminderAt && (
            <p
              className={`text-xs ${isOverdue ? "text-amber-600 font-medium" : "text-muted-foreground"}`}
            >
              {isOverdue ? (
                <>
                  <AlertTriangle className="inline h-3 w-3 mr-1" />
                  {t("reminders.overdue", "Overdue")}
                </>
              ) : (
                <>
                  <Clock className="inline h-3 w-3 mr-1" />
                  {t("reminders.nextReminder", "Next: {{date}}", {
                    date: formatDateShort(recipient.nextReminderAt, user),
                  })}
                </>
              )}
            </p>
          )}
        </div>

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onSendReminder(envelopeId, recipient.id)}
                disabled={sending}
              >
                {sending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {t("reminders.sendNow", "Send reminder now")}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  );
};

// Envelope card component
const EnvelopeCard = ({
  envelope,
  onSendReminder,
  onSendAll,
  sendingRecipient,
  sendingAll,
}) => {
  const { t } = useTranslation();
  const user = useAuthStore((state) => state.user);

  const overdueCount = envelope.recipients.filter(
    (r) => r.nextReminderAt && new Date(r.nextReminderAt) < new Date(),
  ).length;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="min-w-0 flex-1">
            <CardTitle className="text-base font-medium truncate">
              <Link
                to={`/envelopes/${envelope.id}`}
                className="hover:underline"
              >
                {envelope.title}
              </Link>
            </CardTitle>
            <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {t("reminders.sentOn", "Sent {{date}}", {
                  date: formatDateShort(envelope.sentAt, user),
                })}
              </span>
              {envelope.expiresAt && (
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {t("reminders.expires", "Expires {{date}}", {
                    date: formatDateShort(envelope.expiresAt, user),
                  })}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {overdueCount > 0 && (
              <Badge
                variant="outline"
                className="bg-amber-50 text-amber-700 border-amber-200"
              >
                <AlertTriangle className="h-3 w-3 mr-1" />
                {overdueCount} overdue
              </Badge>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => onSendAll(envelope.id)}
              disabled={sendingAll === envelope.id}
            >
              {sendingAll === envelope.id ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              {t("reminders.remindAll", "Remind All")}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="border rounded-lg divide-y">
          {envelope.recipients.map((recipient) => (
            <RecipientRow
              key={recipient.id}
              recipient={recipient}
              envelopeId={envelope.id}
              onSendReminder={onSendReminder}
              sending={sendingRecipient === recipient.id}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// Loading skeleton
const RemindersSkeleton = () => (
  <div className="space-y-6">
    <div className="grid gap-4 md:grid-cols-3">
      {[1, 2, 3].map((i) => (
        <Card key={i}>
          <CardContent className="p-6">
            <Skeleton className="h-4 w-32 mb-2" />
            <Skeleton className="h-8 w-16" />
          </CardContent>
        </Card>
      ))}
    </div>
    <Card>
      <CardHeader>
        <Skeleton className="h-5 w-48" />
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      </CardContent>
    </Card>
  </div>
);

// Empty state
const EmptyState = () => {
  const { t } = useTranslation();

  return (
    <Card>
      <CardContent className="flex flex-col items-center justify-center py-16">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100 mb-4">
          <CheckCircle className="h-8 w-8 text-green-600" />
        </div>
        <h3 className="text-lg font-semibold mb-1">
          {t("reminders.allCaughtUp", "All caught up!")}
        </h3>
        <p className="text-sm text-muted-foreground text-center max-w-sm">
          {t(
            "reminders.noPendingReminders",
            "There are no pending reminders. All your recipients have either signed or are up to date.",
          )}
        </p>
      </CardContent>
    </Card>
  );
};

// Main component
export const Reminders = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [sendingRecipient, setSendingRecipient] = useState(null);
  const [sendingAll, setSendingAll] = useState(null);

  // Query pending reminders
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["reminders", "pending"],
    queryFn: async () => {
      const response = await reminderApi.getPending();
      return response.data.data;
    },
    refetchInterval: 60000, // Refresh every minute
  });

  // Mutation: Send individual reminder
  const sendReminderMutation = useMutation({
    mutationFn: ({ envelopeId, recipientId }) => {
      setSendingRecipient(recipientId);
      return reminderApi.sendImmediate(envelopeId, recipientId);
    },
    onSuccess: () => {
      toast.success(t("reminders.reminderSent", "Reminder sent"));
      queryClient.invalidateQueries({ queryKey: ["reminders", "pending"] });
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message ||
          t("reminders.sendFailed", "Failed to send reminder"),
      );
    },
    onSettled: () => {
      setSendingRecipient(null);
    },
  });

  // Mutation: Send all reminders for envelope
  const sendAllMutation = useMutation({
    mutationFn: (envelopeId) => {
      setSendingAll(envelopeId);
      return reminderApi.sendToAll(envelopeId);
    },
    onSuccess: (response) => {
      const { sent } = response.data.data;
      toast.success(
        t("reminders.remindersSentCount", "{{count}} reminder(s) sent", {
          count: sent,
        }),
      );
      queryClient.invalidateQueries({ queryKey: ["reminders", "pending"] });
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message ||
          t("reminders.sendFailed", "Failed to send reminders"),
      );
    },
    onSettled: () => {
      setSendingAll(null);
    },
  });

  const handleSendReminder = (envelopeId, recipientId) => {
    sendReminderMutation.mutate({ envelopeId, recipientId });
  };

  const handleSendAll = (envelopeId) => {
    sendAllMutation.mutate(envelopeId);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold font-heading tracking-tight">
            {t("reminders.title", "Reminders")}
          </h1>
          <p className="text-muted-foreground">
            {t(
              "reminders.subtitle",
              "Manage signing reminders for your envelopes",
            )}
          </p>
        </div>
        <RemindersSkeleton />
      </div>
    );
  }

  const { envelopes = [], summary = {} } = data || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("reminders.title", "Reminders")}
          </h1>
          <p className="text-muted-foreground">
            {t(
              "reminders.subtitle",
              "Manage signing reminders for your envelopes",
            )}
          </p>
        </div>
        <Button variant="outline" onClick={() => refetch()}>
          <RefreshCw className="h-4 w-4 mr-2" />
          {t("common.refresh", "Refresh")}
        </Button>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <StatCard
          title={t("reminders.stats.pendingEnvelopes", "Pending Envelopes")}
          value={summary.totalEnvelopes || 0}
          description={t(
            "reminders.stats.awaitingSignature",
            "Awaiting signatures",
          )}
          icon={Mail}
        />
        <StatCard
          title={t("reminders.stats.pendingRecipients", "Pending Recipients")}
          value={summary.totalPendingRecipients || 0}
          description={t("reminders.stats.needsAction", "Need to sign")}
          icon={User}
        />
        <StatCard
          title={t("reminders.stats.overdueReminders", "Overdue Reminders")}
          value={summary.overdueReminders || 0}
          description={t("reminders.stats.readyToSend", "Ready to send")}
          icon={AlertTriangle}
          variant={summary.overdueReminders > 0 ? "warning" : "default"}
        />
      </div>

      {/* Envelopes list */}
      {envelopes.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold">
            {t("reminders.pendingEnvelopes", "Pending Envelopes")}
          </h2>
          {envelopes.map((envelope) => (
            <EnvelopeCard
              key={envelope.id}
              envelope={envelope}
              onSendReminder={handleSendReminder}
              onSendAll={handleSendAll}
              sendingRecipient={sendingRecipient}
              sendingAll={sendingAll}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Reminders;
